import React, { Dispatch, useRef } from "react";
import Dropdown from "react-bootstrap/Dropdown";

import TopBarLogo from "./TopBarLogo";
import DeviceTypeEnum from "features/shared/enums/DeviceTypeEnum";
import NotificationsMenu from "./NotificationsMenu";

import styles from "./topbar.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { UserState } from "redux-oidc";
import IAppState from "infrastructure/store/IAppState";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import IAlertsState from "infrastructure/store/alerts/IAlertsState";
import {
  setSidebarShow,
  SidebarAction,
} from "infrastructure/store/sidebar/SidebarActions";
import SidebarState from "infrastructure/store/sidebar/SidebarState";
import MessagesMenu from "./MessagesMenu";
import IMessagesState from "infrastructure/store/messages/IMessagesState";
import { loadPortalConfig } from "features/shared/helpers/modules";
import RenderWrapper from "features/shared/components/renderWrapper/RenderWrapper";
import TopBarHeading from './TopBarHeading';
import { useTranslation } from "react-i18next";
import LanguageDropdown from "./LanguageDropdown";

const portalConfig = loadPortalConfig();

interface Props {
  setProfileDialogShow: React.Dispatch<React.SetStateAction<boolean>>;
}

const TopBar = ({ setProfileDialogShow }: Props) => {
  // translations
  const tLabels = useTranslation("shared\\components").t;

  const bellRef = useRef<any>();
  const messagesDropdownRef = useRef<any>();

  // STORE
  const sidebarDispatch = useDispatch<Dispatch<SidebarAction>>();
  const sidebarShow = useSelector<IAppState, SidebarState>(
    (store) => store.sidebar
  ).sidebarShow;

  const userState = useSelector<IAppState, UserState>((store) => store.oidc);
  const alerts = useSelector<IAppState, IAlertsState>((store) => store.alerts)
    .alerts;
  const messages = useSelector<IAppState, IMessagesState>(
    (store) => store.messages
  );

  // handlers
  const toggleSidebarHandler = () => {
    sidebarDispatch(setSidebarShow(!sidebarShow));

    // disable page scroll on mobile devices
    // 768px = default bootstrap md breakpoint
    if (!window.matchMedia("(min-width: 768px)").matches) {
      document.body.classList.add("overflow-hidden");
    }
  };

  const showProfileDialogHandler = () => {
    setProfileDialogShow(true);
  };

  return (
    <nav className={`navbar navbar-expand ${styles.topbar}`}>
      <div className={styles.topbarContainer}>
        <TopBarLogo type={DeviceTypeEnum.DESKTOP} />
        <button
          type="button"
          className={`btn ${styles.topbarMenuBtn}`}
          onClick={toggleSidebarHandler}
        >
          <FontAwesomeIcon icon="bars" />
        </button>
        <TopBarHeading />
        <div className={styles.topbarActionsWrapper}>
          <LanguageDropdown />
          <RenderWrapper isVisible={() => portalConfig.useMessages}>
            <Dropdown
              ref={messagesDropdownRef}
              alignRight
              className={styles.topbarActionItem}
            >
              <Dropdown.Toggle
                id="dropdown-messages-menu"
                className={`shadow-none ${styles.topbarActionLink}`}
              >
                {messages.messages.unreadedCount > 0 &&
                  <span className={styles.topbarInfoNumber}>
                    {messages.messages.unreadedCount}
                  </span>
                }
                <FontAwesomeIcon icon="envelope" />
              </Dropdown.Toggle>
              <MessagesMenu
                title={tLabels("topBar.messages")}
                messages={messages.messages.result}
                onClickMessageItem={() => {}}
                messagesDropdownRef={messagesDropdownRef}
              />
            </Dropdown>
          </RenderWrapper>

          <Dropdown ref={bellRef} alignRight className={styles.topbarActionItem}>
            <Dropdown.Toggle
              id="dropdown-notifications-menu"
              className={`shadow-none ${styles.topbarActionLink}`}
            >
              {alerts.length > 0 && (
                <span className={styles.topbarInfoNumber}>{alerts.length}</span>
              )}
              <FontAwesomeIcon icon="bell" />
            </Dropdown.Toggle>
            <NotificationsMenu
              title={tLabels("topBar.alerts")}
              notifications={alerts}
              bellRef={bellRef}
            />
          </Dropdown>
          <div className={`${styles.topbarActionItem} ${styles.topbarActionProfileItem}`}>
            <button
              className={`btn shadow-none ${styles.topbarActionLink}`}
              onClick={showProfileDialogHandler}
            >
              <span className={`mr-sm-2 ${styles.topbarProfileAvatar}`}>
                <FontAwesomeIcon icon="user" />
              </span>
              <span
                className={`d-none d-sm-inline-block text-truncate ${styles.topbarProfileText}`}
              >
                {userState.user?.profile.email}
              </span>
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default TopBar;
